import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Printer, ChevronLeft, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WOUND_LABELS = {
  ulcera_venosa: "Úlcera Venosa",
  pe_diabetico: "Pé Diabético",
  lesao_por_pressao: "Lesão por Pressão",
};
const RISK_LABELS = { critico: "CRÍTICO", alto: "ALTO", medio: "MÉDIO", baixo: "BAIXO" };
const RISK_COLORS_PRINT = { critico: "#dc2626", alto: "#ea580c", medio: "#d97706", baixo: "#16a34a" };

function getSessionToken() {
  const match = document.cookie.match(/session_token=([^;]+)/);
  return match ? match[1] : "";
}

export default function RelatorioPage() {
  const { woundId } = useParams();
  const navigate = useNavigate();
  const [wound, setWound] = useState(null);
  const [patient, setPatient] = useState(null);
  const [evolutions, setEvolutions] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const sessionToken = getSessionToken();
  const printedRef = useRef(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const wResp = await axios.get(`${API}/wounds/${woundId}`, { withCredentials: true });
        const w = wResp.data;
        setWound(w);
        // Fetch patient
        if (w.patient_id) {
          const pResp = await axios.get(`${API}/patients/${w.patient_id}`, { withCredentials: true });
          setPatient(pResp.data);
        }
        // Evolutions and photos
        setEvolutions(w.evolutions || []);
        setPhotos(w.photos || []);
      } catch (e) {
        setError("Não foi possível carregar os dados do relatório.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [woundId]);

  // Auto-print after data is loaded
  useEffect(() => {
    if (!loading && wound && !printedRef.current) {
      printedRef.current = true;
      setTimeout(() => window.print(), 500);
    }
  }, [loading, wound]);

  const getPhotoUrl = (photo) =>
    `${API}/photos/download/${photo.storage_path}?auth=${sessionToken}`;

  // Group photos by evolution_id
  const photosByEvo = photos.reduce((acc, p) => {
    const key = p.evolution_id || "avulso";
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Preparando relatório...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => navigate(-1)} className="text-sm underline text-gray-600">Voltar</button>
        </div>
      </div>
    );
  }

  const printDate = new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <>
      {/* Screen-only controls */}
      <div className="print:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 flex items-center justify-between px-4 py-3 z-50 shadow-sm">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <p className="text-sm font-medium text-gray-700">Pré-visualização do Relatório</p>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-1.5 text-sm bg-gray-900 text-white px-3 py-1.5 rounded-lg hover:bg-gray-700 transition-all"
          data-testid="print-report-btn"
        >
          <Printer className="w-3.5 h-3.5" /> Imprimir / Salvar PDF
        </button>
      </div>

      {/* Print body */}
      <div className="print:mt-0 mt-16 max-w-3xl mx-auto p-8 print:p-0 bg-white min-h-screen" data-testid="relatorio-page">
        <style>{`
          @media print {
            @page { margin: 1.5cm; size: A4; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .print\\:hidden { display: none !important; }
            .page-break { page-break-before: always; }
          }
        `}</style>

        {/* Header */}
        <div className="flex items-start justify-between pb-5 border-b-2 border-gray-900 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-green-700" />
              <span className="text-xs font-bold text-green-700 tracking-widest uppercase">RedeCicatriza</span>
            </div>
            <h1 className="text-2xl font-black text-gray-900" style={{ fontFamily: "Georgia, serif" }}>
              Relatório de Acompanhamento de Ferida
            </h1>
            <p className="text-sm text-gray-500 mt-1">Gerado em {printDate}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 uppercase tracking-wider">Ref.</p>
            <p className="text-sm font-mono text-gray-600">{woundId?.slice(0, 8).toUpperCase()}</p>
          </div>
        </div>

        {/* Patient + Wound info */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Paciente</p>
            <p className="text-base font-bold text-gray-900">{patient?.name}</p>
            {patient?.date_of_birth && (
              <p className="text-sm text-gray-600">
                Nascimento: {new Date(patient.date_of_birth).toLocaleDateString("pt-BR")}
              </p>
            )}
            {patient?.comorbidities?.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">
                Comorbidades: {patient.comorbidities.join(", ")}
                {patient.other_comorbidities && `, ${patient.other_comorbidities}`}
              </p>
            )}
            {patient?.health_unit_name && (
              <p className="text-xs text-gray-500 mt-1">Unidade: {patient.health_unit_name}</p>
            )}
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Lesão</p>
            <p className="text-base font-bold text-gray-900">{WOUND_LABELS[wound?.wound_type] || wound?.wound_type}</p>
            <p className="text-sm text-gray-600">{wound?.location}</p>
            {wound?.start_date && (
              <p className="text-xs text-gray-500 mt-1">
                Início: {new Date(wound.start_date + "T12:00:00").toLocaleDateString("pt-BR")}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Status: <span className="font-medium">{wound?.status === "active" ? "Ativa" : wound?.status === "closed" ? "Fechada" : wound?.status}</span>
            </p>
          </div>
        </div>

        {/* Evolutions */}
        <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200" style={{ fontFamily: "Georgia, serif" }}>
          Histórico de Evoluções ({evolutions.length})
        </h2>

        {evolutions.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Nenhuma evolução registrada.</p>
        ) : (
          <div className="space-y-6">
            {[...evolutions].reverse().map((evo, i) => {
              const evoPhotos = photosByEvo[evo.evolution_id] || [];
              const ai = evo.ai_analysis || {};
              const riskColor = RISK_COLORS_PRINT[ai.nivel_risco] || "#6b7280";
              return (
                <div key={evo.evolution_id} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Evolution header */}
                  <div className="bg-gray-100 px-4 py-2.5 flex items-center justify-between border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-gray-700">
                        #{evolutions.length - i} — {evo.date
                          ? new Date(evo.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
                          : "Data não informada"}
                      </span>
                      {evo.professional_name && (
                        <span className="text-xs text-gray-500">Prof.: {evo.professional_name}</span>
                      )}
                    </div>
                    {ai.nivel_risco && (
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded border"
                        style={{ color: riskColor, borderColor: riskColor, backgroundColor: `${riskColor}15` }}
                      >
                        {RISK_LABELS[ai.nivel_risco]}
                      </span>
                    )}
                  </div>

                  <div className="p-4 space-y-3">
                    {/* Clinical measurements */}
                    {(evo.length_cm || evo.width_cm) && (
                      <div className="grid grid-cols-3 gap-3 text-center">
                        {[["Comprimento", evo.length_cm], ["Largura", evo.width_cm], ["Profundidade", evo.depth_cm]].map(([l, v]) => (
                          <div key={l} className="bg-gray-50 rounded p-2 border border-gray-200">
                            <p className="text-xs text-gray-400">{l}</p>
                            <p className="text-sm font-bold text-gray-900">{v || "—"} cm</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Tissue, exudate */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {evo.tissue_types?.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500">Tecido: </span>
                          <span className="text-gray-700">{evo.tissue_types.join(", ")}</span>
                        </div>
                      )}
                      {evo.exudate_amount && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500">Exsudato: </span>
                          <span className="text-gray-700">{evo.exudate_amount} {evo.exudate_type && `/ ${evo.exudate_type}`}</span>
                        </div>
                      )}
                      {evo.pain_score !== undefined && evo.pain_score !== null && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500">Dor (EVA): </span>
                          <span className="text-gray-700">{evo.pain_score}/10</span>
                        </div>
                      )}
                      {evo.odor && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500">Odor: </span>
                          <span className="text-gray-700">{evo.odor}</span>
                        </div>
                      )}
                    </div>

                    {/* Infection signs */}
                    {evo.infection_signs?.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded p-2">
                        <span className="text-xs font-bold text-red-700">Sinais de Infecção: </span>
                        <span className="text-xs text-red-700">{evo.infection_signs.join(", ")}</span>
                      </div>
                    )}

                    {/* Notes */}
                    {evo.notes && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Observações:</p>
                        <p className="text-sm text-gray-700 italic">{evo.notes}</p>
                      </div>
                    )}

                    {/* AI Analysis */}
                    {ai.resumo_clinico && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <p className="text-xs font-bold text-blue-700 mb-1">Análise de IA (Suporte à Decisão)</p>
                        <p className="text-sm text-gray-700">{ai.resumo_clinico}</p>
                        {ai.sugestao_protocolo && (
                          <p className="text-xs text-gray-600 mt-1.5">
                            <span className="font-semibold">Protocolo sugerido: </span>{ai.sugestao_protocolo}
                          </p>
                        )}
                        {ai.bandeiras_vermelhas?.length > 0 && (
                          <div className="mt-1.5">
                            <span className="text-xs font-bold text-red-700">Bandeiras: </span>
                            <span className="text-xs text-red-700">{ai.bandeiras_vermelhas.join(" · ")}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Dressing description */}
                    {evo.dressing_description && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Curativo Realizado:</p>
                        <p className="text-sm text-gray-700">{evo.dressing_description}</p>
                      </div>
                    )}

                    {/* Photos */}
                    {evoPhotos.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2">
                          Fotos ({evoPhotos.length}):
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {evoPhotos.map((photo) => (
                            <div key={photo.photo_id} className="relative">
                              <img
                                src={getPhotoUrl(photo)}
                                alt={photo.notes || "Foto"}
                                className="w-full aspect-square object-cover rounded border border-gray-200"
                                crossOrigin="use-credentials"
                              />
                              {photo.notes && (
                                <p className="text-[10px] text-center text-gray-500 mt-0.5">{photo.notes}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-5 border-t border-gray-200">
          <p className="text-xs text-gray-400 mb-4">
            Este relatório é gerado automaticamente pela plataforma RedeCicatriza e destina-se ao uso clínico interno.
            A análise de IA é um suporte à decisão — não substitui o julgamento do profissional de saúde.
          </p>

          {/* Assinaturas eletrônicas das evoluções */}
          {evolutions.filter(e => e.signature).length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wider">Assinaturas Eletrônicas (Lei 14.063/2020)</p>
              <div className="space-y-2">
                {evolutions.filter(e => e.signature).map(e => (
                  <div key={e.evolution_id} className="flex items-start gap-2">
                    <svg className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <p className="text-[10px] text-gray-600 leading-relaxed">{e.signature.signature_block}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-end justify-between">
            <p className="text-xs text-gray-400">Impresso em: {printDate}</p>
            {evolutions.filter(e => e.signature).length === 0 && (
              <div className="text-right">
                <p className="text-xs text-gray-500 font-medium">Profissional: ____________________________</p>
                <p className="text-xs text-gray-500 mt-1">COREN/CRM: _______________  Data: {printDate}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
